import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import { fetchBlog } from "@/lib/shopify";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

const BLOG_META: Record<string, { title: string; eyebrow: string; description: string }> = {
  "the-herbal-codex": {
    title: "Natural Perfumery Primer",
    eyebrow: "The Herbal Codex",
    description:
      "An evolving primer on natural perfumery — sillage, accords, the language of scent.",
  },
  "materia-botanica": {
    title: "Materia Botanica",
    eyebrow: "Apothecary Profiles",
    description:
      "Long-form portraits of the materials we work with — history, chemistry, folklore.",
  },
  "perfume-chapters": {
    title: "Perfume Chapters",
    eyebrow: "Fragrance Stories",
    description: "The mythologies behind each bottle — short fictions and field notes.",
  },
};

const blogQueryOptions = (handle: string) =>
  queryOptions({
    queryKey: ["shopify-blog", handle],
    queryFn: () => fetchBlog(handle),
  });

export const Route = createFileRoute("/field-notes/$blog/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(blogQueryOptions(params.blog)),
  head: ({ params }) => {
    const meta = BLOG_META[params.blog];
    const title = meta ? `${meta.title} — House of Apocrypha` : "Field Notes — House of Apocrypha";
    const path = `https://houseofapocrypha.com/field-notes/${params.blog}`;
    return {
      meta: [
        { title },
        { name: "description", content: meta?.description ?? "Field Notes from House of Apocrypha." },
        { property: "og:title", content: title },
        { property: "og:description", content: meta?.description ?? "" },
        { property: "og:url", content: path },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
              { "@type": "ListItem", position: 2, name: "Field Notes", item: "https://houseofapocrypha.com/field-notes" },
              { "@type": "ListItem", position: 3, name: meta?.title ?? params.blog.replace(/-/g, " "), item: path },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="text-3xl text-parchment">This journal isn't here yet</h1>
        <Link to="/field-notes" className="mt-8 inline-block eyebrow text-gold">
          Return to Field Notes
        </Link>
      </div>
    </div>
  ),
  component: BlogIndex,
});

function BlogIndex() {
  const { blog } = Route.useParams();
  const { data } = useSuspenseQuery(blogQueryOptions(blog));
  const meta = BLOG_META[blog];

  if (!data) throw notFound();
  const articles = data.articles?.edges?.map((e: any) => e.node) ?? [];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <main className="mx-auto max-w-5xl px-6 py-20 lg:py-28">
        <nav className="text-sm">
          <Link to="/field-notes" className="text-muted-foreground hover:text-gold">
            Field Notes
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-parchment">{meta?.title ?? data.title}</span>
        </nav>

        <div className="mt-10 text-center">
          {meta?.eyebrow && <p className="eyebrow">{meta.eyebrow}</p>}
          <h1 className="mt-4 text-4xl text-parchment sm:text-5xl">
            {meta?.title ?? data.title}
          </h1>
          {meta?.description && (
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              {meta.description}
            </p>
          )}
          <div className="ornament mt-10">
            <span>✶</span>
          </div>
        </div>

        {articles.length === 0 ? (
          <p className="mt-20 text-center text-muted-foreground">
            New entries are being prepared. Return soon.
          </p>
        ) : (
          <div className="mt-16 space-y-12">
            {articles.map((a: any, i: number) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="grid gap-8 border-b border-border pb-12 last:border-0 md:grid-cols-[1fr_2fr]"
              >
                {a.image && (
                  <Link
                    to="/field-notes/$blog/$article"
                    params={{ blog, article: a.handle }}
                    className="block overflow-hidden border border-border"
                  >
                    <ResponsiveImage
                      src={a.image.url}
                      alt={a.image.altText || `Illustration for the field note “${a.title}”`}
                      sizes={SIZES.card}
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </Link>
                )}
                <div>
                  <h2 className="font-display text-2xl text-parchment sm:text-3xl">
                    <Link
                      to="/field-notes/$blog/$article"
                      params={{ blog, article: a.handle }}
                      className="hover:text-gold"
                    >
                      {a.title}
                    </Link>
                  </h2>
                  {a.excerpt && (
                    <p className="mt-4 leading-relaxed text-muted-foreground">{a.excerpt}</p>
                  )}
                  <div className="mt-6">
                    <Link
                      to="/field-notes/$blog/$article"
                      params={{ blog, article: a.handle }}
                      className="inline-block border-b border-gold/50 pb-1 font-display text-xs uppercase tracking-[0.24em] text-gold hover:border-gold hover:text-gold-soft"
                    >
                      Continue reading →
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
